// CompanyManagement.tsx - Updated with User Management Integration
import React, { useState, useEffect, useCallback } from 'react';
import './CompanyManagement.css';
import { OrganizationForm } from './OrganizationForm';
import { OrganizationList } from './OrganizationList';
import { SuccessMessageBanner } from './SuccessMessageBanner';
import { ErrorMessageBanner } from './ErrorMessageBanner';
import { FunctionsManagement } from '../functionsManagement/FunctionsManagement';
// NEW: Import User Management Components
import { UserList } from '../focalPointsManagement/UserList';
import { UserForm } from '../focalPointsManagement/UserForm';
import { FocalPointsApiService } from '../focalPointsManagement/apiService';
import { ApiService } from './apiService';
import { 
  Organization, 
  Region, 
  OrganizationManagementProps,
  StatusMessage
} from './types';
// NEW: Import User Management Types
import {
  User,
  Company,
  Function
} from '../focalPointsManagement/types';


export const CompanyManagement: React.FC<OrganizationManagementProps> = ({
  getAccessToken,
  onStatusUpdate
}) => {
  // State management
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);
  
  // Functions management state
  const [showFunctionsManagement, setShowFunctionsManagement] = useState(false);
  const [selectedCompanyForFunctions, setSelectedCompanyForFunctions] = useState<{
    companyId: number;
    companyName: string;
  } | null>(null);

  // NEW: User management state
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState<Company | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [functions, setFunctions] = useState<Function[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  // Success banner state
  const [successBanner, setSuccessBanner] = useState({
    isVisible: false,
    message: ''
  });

  // Error banner state
  const [errorBanner, setErrorBanner] = useState({
    isVisible: false,
    message: ''
  });
  
  // API service instances
  const [apiService] = useState(() => new ApiService(getAccessToken));
  const [userApiService] = useState(() => new FocalPointsApiService(getAccessToken));

  // Internal status update handler that manages both success and error banners
  const handleInternalStatusUpdate = useCallback((status: StatusMessage) => {
    if (status.type === 'success' ) {
      // Show success banner for success and info messages
      setSuccessBanner({
        isVisible: true,
        message: status.message
      });
    } else if (status.type === 'error') {
      // Show error banner for error messages
      setErrorBanner({
        isVisible: true,
        message: status.message
      });
    } else {
      // Pass other types to parent component (if any)
      onStatusUpdate(status);
    }
  }, [onStatusUpdate]);

  // Close success banner
  const closeSuccessBanner = useCallback(() => {
    setSuccessBanner({
      isVisible: false,
      message: ''
    });
  }, []);

  // Close error banner
  const closeErrorBanner = useCallback(() => {
    setErrorBanner({
      isVisible: false,
      message: ''
    });
  }, []);

  // Load regions
  const loadRegions = useCallback(async () => {
    setIsLoadingRegions(true);
    
    const result = await apiService.loadRegions();
    
    if (result.success && result.data) {
      setRegions(result.data);
    } else {
      handleInternalStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load regions'
      });
    }
    
    setIsLoadingRegions(false);
  }, [apiService, handleInternalStatusUpdate]);

  // Load organizations
  const loadOrganizations = useCallback(async (isRefresh = false) => {
    setIsLoading(true);
    
    const result = await apiService.loadOrganizations();
    
    if (result.success && result.data) {
      setOrganizations(result.data);
      // Only show load success message on initial load, not on refresh
      // if (result.message && !isRefresh) {
      //   handleInternalStatusUpdate({
      //     type: 'success',
      //     message: result.message
      //   });
      // }
    } else {
      handleInternalStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load organizations'
      });
    }
    
    setIsLoading(false);
  }, [apiService, handleInternalStatusUpdate]);

  // NEW: Load users for a specific company
  const loadUsersForCompany = useCallback(async (companyId: number) => {
    setIsLoadingUsers(true);
    
    const result = await userApiService.loadUsers(companyId);
    
    if (result.success && result.data) {
      // Filter for company users only
      const companyUsers = result.data.filter(user => 
        user.companyId === companyId && user.userType === 'company'
      );
      setUsers(companyUsers);
      
      // if (result.message) {
      //   handleInternalStatusUpdate({
      //     type: 'success',
      //     message: `Loaded ${companyUsers.length} company users`
      //   });
      // }
    } else {
      setUsers([]);
      handleInternalStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load users'
      });
    }
    
    setIsLoadingUsers(false);
  }, [userApiService, handleInternalStatusUpdate]);

  // NEW: Load functions for a specific company
  const loadFunctionsForCompany = useCallback(async (companyId: number) => {
    setIsLoadingFunctions(true);
    
    const result = await userApiService.loadFunctionsForCompany(companyId);
    
    if (result.success && result.data) {
      setFunctions(result.data);
    } else {
      setFunctions([]);
      // Don't show error for functions as it's not critical
    }
    
    setIsLoadingFunctions(false);
  }, [userApiService]);

  // Load data on component mount
  useEffect(() => {
    loadRegions();
    loadOrganizations(false); // Pass false for initial load (show success message)
  }, [loadRegions, loadOrganizations]);

  // Handle create new organization
  const handleCreateNew = () => {
    setEditingOrganization(null);
    setShowForm(true);
  };

  // Handle edit organization
  const handleEdit = (organization: Organization) => {
    setEditingOrganization(organization);
    setShowForm(true);
  };

  // Handle back to list
  const handleBack = () => {
    setShowForm(false);
    setEditingOrganization(null);
    setShowFunctionsManagement(false);
    setSelectedCompanyForFunctions(null);
    // NEW: Reset user management state
    setShowUserManagement(false);
    setShowUserForm(false);
    setSelectedCompanyForUsers(null);
    setUsers([]);
    setFunctions([]);
    setEditingUser(null);
  };

  // Handle successful form submission
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingOrganization(null);
    loadOrganizations(true); // Pass true to indicate this is a refresh
  };

  // Create a refresh function that passes the isRefresh flag
  const handleRefresh = useCallback(() => {
    loadOrganizations(true); // Pass true to indicate this is a refresh
  }, [loadOrganizations]);

  // Handle manage divisions
  const handleManageDivisions = (organization: Organization) => {
    setSelectedCompanyForFunctions({
      companyId: organization.companyId,
      companyName: organization.companyName
    });
    setShowFunctionsManagement(true);
  };

  // Handle back from functions management
  const handleBackFromFunctions = () => {
    setShowFunctionsManagement(false);
    setSelectedCompanyForFunctions(null);
  };

  // NEW: Handle manage users
  const handleManageUsers = (organization: Organization) => {
    const companyData: Company = {
      companyId: organization.companyId,
      companyName: organization.companyName,
      companyDescription: undefined, // Organization doesn't have companyDescription
      isActive: true,
      authorized: organization.authorized,
      blacklisted: organization.blacklisted,
      createdAt: organization.createdAt,
      updatedAt: organization.updatedAt
    };
    
    setSelectedCompanyForUsers(companyData);
    setShowUserManagement(true);
    
    // Load users and functions for this company
    loadUsersForCompany(organization.companyId);
    loadFunctionsForCompany(organization.companyId);
  };

  // NEW: Handle back from user management
  const handleBackFromUsers = () => {
    setShowUserManagement(false);
    setShowUserForm(false);
    setSelectedCompanyForUsers(null);
    setUsers([]);
    setFunctions([]);
    setEditingUser(null);
  };

  // NEW: Handle create new user
  const handleCreateNewUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  // NEW: Handle edit user
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  // NEW: Handle back from user form
  const handleBackFromUserForm = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };

  // NEW: Handle successful user form submission
  const handleUserFormSuccess = () => {
    setShowUserForm(false);
    setEditingUser(null);
    // Reload users for the current company
    if (selectedCompanyForUsers) {
      loadUsersForCompany(selectedCompanyForUsers.companyId);
    }
  };

  // NEW: Handle refresh users
  const handleRefreshUsers = () => {
    if (selectedCompanyForUsers) {
      loadUsersForCompany(selectedCompanyForUsers.companyId);
    }
  };

  // NEW: Handle change selection (back to company list)
  const handleChangeSelection = () => {
    handleBackFromUsers();
  };

  // Determine which view to show
  const getCurrentView = () => {
    if (showUserForm && selectedCompanyForUsers) {
      return 'user-form';
    } else if (showUserManagement && selectedCompanyForUsers) {
      return 'user-management';
    } else if (showFunctionsManagement && selectedCompanyForFunctions) {
      return 'functions-management';
    } else if (showForm) {
      return 'organization-form';
    } else {
      return 'organization-list';
    }
  };

  // Show form, functions management, user management, or list based on state
  return (
    <>
      {/* Success Message Banner - Top Center */}
      <SuccessMessageBanner
        message={successBanner.message}
        isVisible={successBanner.isVisible}
        onClose={closeSuccessBanner}
        autoCloseDelay={5000}
      />

      {/* Error Message Banner - Top Right */}
      <ErrorMessageBanner
        message={errorBanner.message}
        isVisible={errorBanner.isVisible}
        onClose={closeErrorBanner}
        autoCloseDelay={7000} // Slightly longer for error messages
      />

      {getCurrentView() === 'user-form' ? (
        /* NEW: User Form View */
        <div className="company-management">
          <div className="breadcrumb-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px' }}>
            <button 
              onClick={handleBackFromUserForm}
              className="breadcrumb-link"
            >
              ← Back to Users
            </button>
            <h3 className="page-title" style={{ margin: 0 }}>
              {editingUser ? 'Edit User' : 'Add New User'} - {selectedCompanyForUsers?.companyName}
            </h3>
          </div>
          <UserForm
            editingUser={editingUser}
            selectedUserType="company"
            selectedCompany={selectedCompanyForUsers}
            functions={functions}
            isLoadingFunctions={isLoadingFunctions}
            getAccessToken={getAccessToken}
            onStatusUpdate={handleInternalStatusUpdate}
            onBack={handleBackFromUserForm}
            onSuccess={handleUserFormSuccess}
          />
        </div>
      ) : getCurrentView() === 'user-management' ? (
        /* NEW: User Management View */
        <div className="company-management">
  <h1 style={{
    fontSize: '25px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 12px 0',
    padding: '5px 0 0 20px',
    lineHeight: 1.3,
    fontFamily: "'Segoe UI', sans-serif",
    letterSpacing: '-0.01em',
  }}>
    Users - {selectedCompanyForUsers?.companyName}
  </h1>
  <button 
    onClick={handleBackFromUsers}
    className="breadcrumb-link"
    style={{ marginLeft: '20px', marginBottom: '16px' }}
  >
    ← Back to Companies
  </button>
  <UserList
    users={users}
    isLoading={isLoadingUsers}
    selectedUserType="company"
    selectedCompany={selectedCompanyForUsers}
    getAccessToken={getAccessToken}
    onStatusUpdate={handleInternalStatusUpdate}
    onEdit={handleEditUser}
    onCreateNew={handleCreateNewUser}
    onChangeSelection={handleChangeSelection}
    onRefresh={handleRefreshUsers}
  />
</div>
      ) : getCurrentView() === 'functions-management' ? (
        /* Functions Management View */
        <div className="company-management">
          
          <FunctionsManagement
            getAccessToken={getAccessToken}
            onStatusUpdate={handleInternalStatusUpdate}
            companyContext={selectedCompanyForFunctions || undefined}
            onBack={handleBackFromFunctions}
            onManageUsers={() => {
              // Navigate from divisions view to users view for the same company
              if (selectedCompanyForFunctions) {
                const org = organizations.find(o => o.companyId === selectedCompanyForFunctions.companyId);
                if (org) {
                  setShowFunctionsManagement(false);
                  setSelectedCompanyForFunctions(null);
                  handleManageUsers(org);
                }
              }
            }}
          />
        </div>
      ) : getCurrentView() === 'organization-form' ? (
        <OrganizationForm
          editingOrganization={editingOrganization}
          regions={regions}
          isLoadingRegions={isLoadingRegions}
          getAccessToken={getAccessToken}
          onStatusUpdate={handleInternalStatusUpdate}
          onBack={handleBack}
          onSuccess={handleFormSuccess}
        />
      ) : (
        <OrganizationList
          organizations={organizations}
          isLoading={isLoading}
          getAccessToken={getAccessToken}
          onStatusUpdate={handleInternalStatusUpdate}
          onEdit={handleEdit}
          onCreateNew={handleCreateNew}
          onRefresh={handleRefresh}
          onManageDivisions={handleManageDivisions}
          onManageUsers={handleManageUsers} // NEW: Pass the manage users handler
        />
      )}
    </>
  );

};